import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { GENRES, MOODS } from '../data/mockData';
import { api } from '../lib/api';
import { AI_ROLE_EXPLANATION, AI_ONE_LINER } from '../constants/aiPolicy';

const PROVIDER_LABEL: Record<string, string> = {
  elevenlabs: 'Powered by ElevenLabs Music — your lyrics, vocals and backing music.',
  unconfigured: 'Music generation is not configured yet.',
  test: 'Demo mode — short synthetic beats only; no sung vocals.',
  'hf-space': 'ACE-Step via Hugging Face (experimental).',
  replicate: 'Replicate music generation.',
};

export function StudioPage() {
  const { addSong, currentUser, refreshSongs } = useApp();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [secondaryGenres, setSecondaryGenres] = useState<string[]>([]);
  const [mood, setMood] = useState('');
  const [bpm, setBpm] = useState<number | ''>('');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [contentRating, setContentRating] = useState<'CLEAN' | 'EXPLICIT'>('CLEAN');
  const [privacy, setPrivacy] = useState<'PRIVATE' | 'PUBLIC_REQUESTED'>('PRIVATE');
  const [file, setFile] = useState<File | null>(null);
  const [ownsRights, setOwnsRights] = useState(false);
  const [cleanConfirmed, setCleanConfirmed] = useState(false);
  const [parentConsentConfirmed, setParentConsentConfirmed] = useState(false);
  const [parentPassword, setParentPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedSong, setSavedSong] = useState<{ title: string; id: string } | null>(null);

  // Full-song generation state (one pass: style + your lyrics -> cohesive track)
  const [stylePrompt, setStylePrompt] = useState('');
  const [songStatus, setSongStatus] = useState<string | null>(null);
  const [songError, setSongError] = useState<string | null>(null);
  const [songJob, setSongJob] = useState<any | null>(null);

  // Metronome state
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [beatCount, setBeatCount] = useState(0);
  const metronomeRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Video state
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoStatus, setVideoStatus] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoJob, setVideoJob] = useState<any | null>(null);
  const videoPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Provider status (engine mode)
  const [providerMode, setProviderMode] = useState<string>('test');
  const songPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api.aiStatus().then((s) => setProviderMode(s.mode)).catch(() => {});
    return () => {
      if (songPollRef.current) clearTimeout(songPollRef.current);
      if (videoPollRef.current) clearTimeout(videoPollRef.current);
      if (metronomeRef.current) clearInterval(metronomeRef.current);
    };
  }, []);

  const wantsPublic = privacy === 'PUBLIC_REQUESTED';
  const genreRequired = wantsPublic;
  const effectiveBpm = bpm === '' ? 90 : Number(bpm);
  const tempoCategory = effectiveBpm < 80 ? 'Slow / Ballad' : effectiveBpm < 100 ? 'Mid-tempo' : effectiveBpm < 120 ? 'Upbeat' : effectiveBpm < 140 ? 'Energetic' : 'Fast / Dance';

  // Metronome
  const startMetronome = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    const ctx = audioCtxRef.current;
    const intervalMs = 60000 / effectiveBpm;
    let count = 0;
    setMetronomeOn(true);
    setBeatCount(0);
    const tick = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = count % 4 === 0 ? 880 : 440;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.05);
      setBeatCount(count + 1); count++;
    };
    tick();
    metronomeRef.current = setInterval(tick, intervalMs);
  };
  const stopMetronome = () => {
    if (metronomeRef.current) clearInterval(metronomeRef.current);
    setMetronomeOn(false); setBeatCount(0);
  };
  const toggleMetronome = () => (metronomeOn ? stopMetronome() : startMetronome());

  // Video
  const buildVideo = async () => {
    setVideoError(null); setVideoStatus(null); setVideoJob(null);
    try {
      const { job } = await api.videoJob({ prompt: videoPrompt });
      setVideoJob(job);
      setVideoStatus(`Video job started (ID: ${job.id}). Processing...`);
      pollVideoJob(job.id);
    } catch (e: any) {
      setVideoError(e?.detail?.message || e?.message || 'Video generation failed');
    }
  };
  const pollVideoJob = (jobId: string) => {
    const poll = async () => {
      try {
        const { job } = await api.aiJobStatus(jobId);
        setVideoJob(job);
        if (job.status === 'COMPLETED') { setVideoStatus('✓ Video ready!'); return; }
        if (job.status === 'FAILED') { setVideoError(job.error || 'Video generation failed'); return; }
        setVideoStatus(`Processing video... (${job.status})`);
        videoPollRef.current = setTimeout(poll, 3000);
      } catch (e: any) { setVideoError(e?.message || 'Polling failed'); }
    };
    poll();
  };

  const canSave =
    title.trim() !== '' &&
    (!genreRequired || genre !== '') &&
    ownsRights &&
    (!wantsPublic || (cleanConfirmed && contentRating === 'CLEAN')) &&
    (!currentUser?.parentalConsent || (parentConsentConfirmed && parentPassword.trim().length >= 4));

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] || null);
  const toggleSecondary = (g: string) =>
    setSecondaryGenres((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setError(null); setSavedSong(null);
    try {
      const song = await addSong({
        file: file || undefined,
        title: title.trim(),
        genre: genre || 'Other', secondaryGenres,
        mood: mood || undefined, bpm: bpm === '' ? undefined : Number(bpm), duration: duration || undefined,
        notes: notes.trim() || undefined, lyrics: lyrics.trim() || undefined,
        contentRating, privacy, ownsRights,
        parentalPassword: currentUser?.parentalConsent ? parentPassword : undefined,
      });
      setSavedSong({ title: song.title, id: song.id });
      setTitle(''); setGenre(''); setSecondaryGenres([]); setMood(''); setBpm(''); setDuration('');
      setNotes(''); setLyrics(''); setContentRating('CLEAN'); setPrivacy('PRIVATE');
      setFile(null); setOwnsRights(false); setCleanConfirmed(false);
      await refreshSongs();
    } catch (err: any) { setError(err?.detail?.message || err?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const saveGeneratedToLibrary = async (job: any) => {
    if (!job || job.status !== 'COMPLETED') return;
    if (!title.trim()) { setError('Enter a song title first, then save.'); return; }
    setSaving(true); setError(null);
    try {
      const song = await addSong({
        title: title.trim(),
        genre: genre || 'Other', secondaryGenres,
        mood: mood || undefined, bpm: bpm === '' ? undefined : Number(bpm),
        duration: duration || undefined, lyrics: lyrics.trim() || undefined,
        notes: notes.trim() || undefined,
        contentRating, privacy: 'PRIVATE', ownsRights: true,
        sourceJobId: job.id, sourceType: 'song',
      });
      setSavedSong({ title: song.title, id: song.id });
      await refreshSongs();
    } catch (err: any) { setError(err?.detail?.message || err?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Full song, one pass: describe style + paste your lyrics -> one cohesive track.
  const buildSong = async () => {
    if (!lyrics.trim()) {
      setSongError('You must type your own lyrics first. IAMMUZIK never writes lyrics for you — the AI only performs your words.');
      return;
    }
    if (!stylePrompt.trim()) {
      setSongError('Describe the style of the song you want (genre, instruments, mood, vocal character).');
      return;
    }
    setSongError(null); setSongStatus(null); setSongJob(null);
    try {
      const { job } = await api.songJob({
        prompt: stylePrompt.trim(),
        lyrics: lyrics.trim(),
        genre: genre || undefined,
        mood: mood || undefined,
        duration: duration || undefined,
      });
      setSongJob(job);
      setSongStatus(`Song job started (ID: ${job.id}). Generating a full track...`);
      pollSongJob(job.id);
    } catch (err: any) {
      const msg = err?.detail?.message || err?.message || 'Request failed';
      setSongError(err?.detail?.error === 'POLICY_VIOLATION' ? `🚫 ${msg}` : msg);
    }
  };

  const pollSongJob = (jobId: string) => {
    const poll = async () => {
      try {
        const { job } = await api.aiJobStatus(jobId);
        setSongJob(job);
        if (job.status === 'COMPLETED') {
          setSongStatus(`✓ Song ready! Provider: ${job.provider || providerMode}.`);
          return;
        }
        if (job.status === 'FAILED') { setSongError(`Generation failed: ${job.error || 'Unknown error'}`); return; }
        setSongStatus(`Generating your song... (${job.status})`);
        songPollRef.current = setTimeout(poll, 3000);
      } catch (e: any) { setSongError(e?.message || 'Polling failed'); }
    };
    poll();
  };

  return (
    <div className="grid cols-2">
      {/* CREATE A FULL SONG (one pass) */}
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="section-title gold">Create a Full Song — One Pass</div>
        <div className="policy-banner">
          <strong>🚫 NO AI LYRICS.</strong> {AI_ONE_LINER} {AI_ROLE_EXPLANATION}
        </div>
        <p className="muted" style={{ fontSize: 14, margin: '8px 0 12px' }}>
          Describe the style, paste your own lyrics, and generate one cohesive track — beat, melody, and
          vocal performance together. No stitching a beat to a vocal. The AI performs your words; it never writes them.
          <br />
          <strong>Provider: {PROVIDER_LABEL[providerMode] ?? PROVIDER_LABEL.test}</strong>
        </p>

        <div className="form-group">
          <label className="label">Describe the style <span className="req">*</span></label>
          <textarea
            className="textarea"
            value={stylePrompt}
            onChange={(e) => setStylePrompt(e.target.value)}
            placeholder="e.g. late-night R&B with warm 808s, sparse piano, and a smooth, soulful male vocal. Laid-back verses, a melodic emotional hook, building energy toward the end."
          />
          <div className="field-hint">Cover genre, instruments, groove, vocal character, and the energy arc. The genres, mood, BPM and length from the Song Details card below are sent with your request.</div>
        </div>

        <div className="flex gap-8">
          <button className="btn btn-gold" onClick={buildSong} disabled={!stylePrompt.trim() || !lyrics.trim()}>
            Generate full song
          </button>
        </div>
        {songStatus && <div className="chip chip-approved mt-16" style={{ marginTop: 12 }}>{songStatus}</div>}
        {songError && <div className="field-hint" style={{ color: 'var(--iam-danger)', marginTop: 8 }}>{songError}</div>}
        {songJob?.status === 'COMPLETED' && songJob?.resultUrl && (
          <div style={{ marginTop: 12 }}>
            <audio controls src={api.audioUrl(songJob.resultUrl)} style={{ width: '100%' }} />
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={() => saveGeneratedToLibrary(songJob)} disabled={saving}>
              {saving ? 'Saving…' : 'Save this song to Library'}
            </button>
          </div>
        )}
      </div>

      {/* SONG DETAILS */}
      <div className="card">
        <div className="section-title">Song Details</div>

        <div className="form-group">
          <label className="label">Song title <span className="req">*</span></label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Midnight Royal" />
        </div>

        <div className="form-group">
          <label className="label">Primary genre {genreRequired && <span className="req">*</span>}{!genreRequired && <span className="muted"> (required for public/battle)</span>}</label>
          <select className="select" value={genre} onChange={(e) => setGenre(e.target.value)}>
            <option value="">Select genre…</option>
            {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="label">Blend with other genres <span className="muted">(optional — mix your sound)</span></label>
          <div className="genre-chips">
            {GENRES.filter((g) => g !== genre && g !== 'Other').map((g) => (
              <button key={g} type="button" className={`chip-btn ${secondaryGenres.includes(g) ? 'on' : ''}`} onClick={() => toggleSecondary(g)}>{g}</button>
            ))}
          </div>
          <div className="field-hint">Pick more than one to mix feels — e.g. Reggae + Pop, Hip-Hop + R&B, Gospel + Country.</div>
        </div>

        <div className="grid cols-3">
          <div className="form-group">
            <label className="label">Mood</label>
            <select className="select" value={mood} onChange={(e) => setMood(e.target.value)}>
              <option value="">Any</option>
              {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="label">BPM</label>
            <input className="input" type="number" value={bpm} onChange={(e) => setBpm(e.target.value === '' ? '' : Number(e.target.value))} placeholder="90" />
            <div className="field-hint">Tempo: {tempoCategory} ({effectiveBpm} BPM)</div>
          </div>
          <div className="form-group">
            <label className="label">Length</label>
            <input className="input" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="3:00" />
          </div>
        </div>

        <div className="form-group">
          <label className="label">Your lyrics <span className="req">*</span> <span className="muted">(for AI song generation)</span></label>
          <textarea className="textarea" rows={14} style={{ minHeight: 280, resize: 'vertical' }} value={lyrics} onChange={(e) => setLyrics(e.target.value)} placeholder={'Paste or type your own lyrics here. You can write naturally; line breaks and [verse], [chorus], [bridge] tags are optional.\n\nIAMMUZIK will never generate lyrics for you.'} />
          <div className="field-hint">{lyrics.length.toLocaleString()} characters · {lyrics.trim() ? lyrics.trim().split(/\s+/).length.toLocaleString() : 0} words. Paste full verses or a whole song; long lines are formatted automatically for music generation. These stay your own words.</div>
        </div>

        <div className="form-group">
          <label className="label">Production notes (optional)</label>
          <textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Arrangement ideas, references, anything you want to remember about this track." />
        </div>

        <div className="form-group">
          <label className="label">Content rating</label>
          <div className="flex gap-8">
            <button type="button" className={`btn btn-sm ${contentRating === 'CLEAN' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setContentRating('CLEAN')}>Clean</button>
            <button type="button" className={`btn btn-sm ${contentRating === 'EXPLICIT' ? 'btn-danger' : 'btn-outline'}`} onClick={() => setContentRating('EXPLICIT')}>Explicit</button>
          </div>
        </div>

        <div className="form-group">
          <label className="label">Privacy</label>
          <div className="flex gap-8">
            <button type="button" className={`btn btn-sm ${privacy === 'PRIVATE' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setPrivacy('PRIVATE')}>Private (default)</button>
            <button type="button" className={`btn btn-sm ${privacy === 'PUBLIC_REQUESTED' ? 'btn-gold' : 'btn-outline'}`} onClick={() => setPrivacy('PUBLIC_REQUESTED')}>Request The Wave</button>
          </div>
        </div>

        <label className="checkbox-row">
          <input type="checkbox" checked={ownsRights} onChange={(e) => setOwnsRights(e.target.checked)} />
          <span>I confirm that I own this music or have all necessary rights and permissions to upload, distribute, submit, and promote it through IAMMUZIK.</span>
        </label>

        {currentUser?.parentalConsent && (
          <>
            <label className="checkbox-row">
              <input type="checkbox" checked={parentConsentConfirmed} onChange={(e) => setParentConsentConfirmed(e.target.checked)} />
              <span>Parental consent: I confirm a parent or guardian has approved this upload.</span>
            </label>
            <div className="form-group">
              <label className="label">Parental password <span className="req">*</span></label>
              <input className="input" type="password" value={parentPassword} onChange={(e) => setParentPassword(e.target.value)} placeholder="Enter parental password" autoComplete="off" />
              <div className="field-hint">Required for users under 18. This password was set during account creation.</div>
            </div>
          </>
        )}

        {wantsPublic && (
          <label className="checkbox-row">
            <input type="checkbox" checked={cleanConfirmed} onChange={(e) => setCleanConfirmed(e.target.checked)} />
            <span>I confirm this version is clean and safe for public placement on The Wave.</span>
          </label>
        )}

        <div className="flex gap-8 mt-16">
          <button className="btn btn-primary btn-block" onClick={handleSave} disabled={!canSave || saving}>{saving ? 'Saving…' : file ? 'Save to Library' : 'Save Song / Draft'}</button>
          <button className="btn btn-outline" onClick={() => navigate('/app/library')}>Cancel</button>
        </div>

        {error && <div className="field-hint" style={{ color: 'var(--iam-danger)' }}>{error}</div>}
        {!canSave && !saving && <div className="field-hint">A title and ownership confirmation are required{wantsPublic ? ', plus a clean version and the public-content confirmation' : ''}. No audio file is needed — you can save a draft, or save an AI-generated song after creating it.</div>}
      </div>

      {/* UPLOAD AUDIO (optional) */}
      <div className="card">
        <div className="section-title">Upload Audio (optional)</div>
        <div className="form-group">
          <label className="label">Audio file <span className="muted">(optional — for your own recorded vocals/tracks)</span></label>
          <input type="file" accept="audio/*" onChange={handleFile} className="input" style={{ padding: 9 }} />
          <div className="field-hint">Selected: {file ? file.name : 'No file chosen'}</div>
          <div className="field-hint">Secure upload to your private storage. Songs are private by default.</div>
        </div>
      </div>

      {/* METRONOME */}
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="section-title gold">Metronome — Hear Your Tempo</div>
        <p className="muted" style={{ fontSize: 14, margin: '8px 0 12px' }}>
          Preview your BPM before generating. Click Start to hear the tempo. This helps you pick the right speed before creating a song — saving you credits on multiple generations.
        </p>
        <div className="flex gap-8 wrap" style={{ alignItems: 'center' }}>
          <button className={metronomeOn ? 'btn btn-danger' : 'btn btn-gold'} onClick={toggleMetronome}>
            {metronomeOn ? '⏹ Stop Metronome' : '▶ Start Metronome'}
          </button>
          <div className="muted" style={{ fontSize: 14 }}>{effectiveBpm} BPM · {tempoCategory}</div>
          {metronomeOn && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="chip chip-gold">Beat {beatCount % 4 === 1 ? '1' : beatCount % 4 === 2 ? '2' : beatCount % 4 === 3 ? '3' : '4'}</span>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                background: beatCount % 4 === 1 ? 'var(--gold-bright)' : 'var(--royal-bright)',
                opacity: metronomeOn ? 1 : 0.3, transition: 'opacity 0.1s',
              }} />
            </div>
          )}
        </div>
        <div className="field-hint mt-16" style={{ marginTop: 8 }}>Tip: Adjust the BPM field above, then restart the metronome to hear the new tempo.</div>
      </div>

      {/* MUSIC VIDEO (Tier 3+) */}
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="section-title gold">Create a Music Video (Tier 3+)</div>
        <div className="policy-banner"><strong>Available on Tier 3 and Tier 4 plans only.</strong> Generate AI music videos from your songs.</div>
        {(currentUser?.tier === 'TIER_3' || currentUser?.tier === 'TIER_4') ? (
          <>
            <p className="muted" style={{ fontSize: 14, margin: '8px 0 12px' }}>
              Describe the visual style for your music video. The AI will generate a video that matches your song's energy and mood.
            </p>
            <div className="flex gap-8">
              <input className="input" value={videoPrompt} onChange={(e) => setVideoPrompt(e.target.value)} placeholder="e.g. dark city streets at night with neon lights and rain" />
              <button className="btn btn-gold" onClick={buildVideo} disabled={!videoPrompt.trim()}>Generate Video</button>
            </div>
            {videoStatus && <div className="chip chip-approved mt-16" style={{ marginTop: 12 }}>{videoStatus}</div>}
            {videoError && <div className="field-hint" style={{ color: 'var(--iam-danger)', marginTop: 8 }}>{videoError}</div>}
            {videoJob?.status === 'COMPLETED' && videoJob?.resultUrl && (
              <div style={{ marginTop: 12 }}>
                <video controls src={api.audioUrl(videoJob.resultUrl)} style={{ width: '100%' }} />
              </div>
            )}
          </>
        ) : (
          <div className="field-hint" style={{ color: 'var(--gold-bright)' }}>
            Music video creation is available on Tier 3 (Pro) and Tier 4 (Studio) plans. Upgrade on the Contracts page to unlock this feature.
          </div>
        )}
      </div>

      <p className="muted" style={{ fontSize: 14, margin: '12px 0 0' }}>Signed in as <strong>{currentUser?.username}</strong>. Anything you save here is your own authentic work.</p>
      {savedSong && <div className="chip chip-approved mt-16" style={{ marginTop: 12 }}>✓ Saved "{savedSong.title}" to your library</div>}
    </div>
  );
}

